import { useRef, useSyncExternalStore } from "react";
import type { StoreSubject } from "./StoreSubject";

export function useStoreSubscribe<T>(subject: StoreSubject<T>): T {
  return useSyncExternalStore(
    subject.subscribe.bind(subject),
    subject.getValue.bind(subject),
    subject.getValue.bind(subject),
  );
}

export function useStoreSelector<T, U>(
  subject: StoreSubject<T>,
  selector: (value: T) => U,
  equals: (previous: U, next: U) => boolean = Object.is,
): U {
  const selectorRef = useRef(selector);
  const equalsRef = useRef(equals);
  const sliceRef = useRef(selector(subject.getValue()));

  selectorRef.current = selector;
  equalsRef.current = equals;

  return useSyncExternalStore(
    onStoreChange => subject.subscribe(() => {
      const nextSlice = selectorRef.current(subject.getValue());
      if (!equalsRef.current(sliceRef.current, nextSlice)) {
        sliceRef.current = nextSlice;
        onStoreChange();
      }
    }),
    () => sliceRef.current,
    () => selectorRef.current(subject.getValue()),
  );
}
